import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Envelope, Lock, DownloadSimple, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Public share landing page — no login required.
 *
 * Flow:
 * 1. Load preview (candidate name, expiry, shared_by) via /api/share/resume/:token/preview
 * 2. Recipient enters email → we POST /request-otp
 * 3. Recipient enters OTP → we POST /verify → receive PDF bytes → open in iframe / new tab
 */
export default function SharedResume() {
    const { token } = useParams();
    const [phase, setPhase] = useState("email");   // email | otp | viewing | invalid | expired
    const [preview, setPreview] = useState(null);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [requesting, setRequesting] = useState(false);

    // Fetch preview on mount
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get(`/share/resume/${token}/preview`);
                if (!alive) return;
                setPreview(r.data);
            } catch (e) {
                if (!alive) return;
                const status = e?.response?.status;
                if (status === 410) setPhase("expired");
                else setPhase("invalid");
                setError(e?.response?.data?.detail || "Share not found");
            }
        })();
        return () => { alive = false; };
    }, [token]);

    const requestOtp = async () => {
        setRequesting(true);
        try {
            await api.post(`/share/resume/${token}/request-otp`, { email });
            toast.success("Access code sent to your email");
            setPhase("otp");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send code");
        } finally {
            setRequesting(false);
        }
    };

    const verify = async () => {
        setLoading(true);
        try {
            const r = await api.post(
                `/share/resume/${token}/verify`,
                { email, otp },
                { responseType: "blob" },
            );
            const blobUrl = URL.createObjectURL(r.data);
            setPdfUrl(blobUrl);
            setPhase("viewing");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Invalid code");
        } finally {
            setLoading(false);
        }
    };

    const download = () => {
        if (!pdfUrl) return;
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `${preview?.candidate_name || "Candidate"}_Resume.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    // ---- Render ----
    if (phase === "invalid" || phase === "expired") {
        return (
            <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white p-8 shadow-sm text-center">
                    <div className="text-cobalt text-4xl mb-3">🔒</div>
                    <h1 className="text-2xl font-black text-ink mb-2">
                        {phase === "expired" ? "Link expired" : "Link not found"}
                    </h1>
                    <p className="text-sm text-[#525252]">
                        {phase === "expired"
                            ? "This share link is no longer valid. Please ask the sender for a new link."
                            : "This share link doesn't exist or has been revoked."}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9FAFB]">
            {/* Brand header */}
            <header className="bg-white border-b border-[#E5E5E5] px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <a href="/" className="text-xl font-black text-cobalt tracking-tight">
                        NaukriDaddy
                    </a>
                    <span className="text-xs text-[#A3A3A3]">
                        Secure resume share
                    </span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-10">
                {/* Header info */}
                <div className="mb-8">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Resume shared with you
                    </p>
                    <h1 className="text-3xl sm:text-4xl font-black text-ink" data-testid="share-candidate-name">
                        {preview?.candidate_name || "…"}
                    </h1>
                    {preview?.shared_by && (
                        <p className="text-sm text-[#525252] mt-1">
                            Shared by <span className="font-bold">{preview.shared_by}</span>
                        </p>
                    )}
                </div>

                {phase === "email" && (
                    <div className="bg-white p-6 sm:p-8 shadow-sm max-w-lg">
                        <div className="flex items-center gap-2 text-cobalt mb-3">
                            <Lock size={18} weight="bold" />
                            <p className="text-sm font-bold uppercase tracking-wider">Verify your identity</p>
                        </div>
                        <p className="text-sm text-[#525252] mb-4">
                            Enter your email to receive a one-time access code. Access is logged for the candidate's privacy.
                        </p>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
                            Your email
                        </label>
                        <input
                            data-testid="share-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            className="w-full border border-[#E5E5E5] px-3 py-2.5 mt-1 mb-3 focus:outline-none focus:border-cobalt"
                            required
                        />
                        <button
                            data-testid="share-request-otp-btn"
                            onClick={requestOtp}
                            disabled={requesting || !email}
                            className="w-full bg-cobalt text-white text-sm font-bold uppercase tracking-wider py-2.5 hover:bg-cobalt/90 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
                        >
                            <Envelope size={14} weight="bold" />
                            {requesting ? "Sending…" : "Send access code"}
                        </button>
                    </div>
                )}

                {phase === "otp" && (
                    <div className="bg-white p-6 sm:p-8 shadow-sm max-w-lg">
                        <div className="flex items-center gap-2 text-cobalt mb-3">
                            <Envelope size={18} weight="bold" />
                            <p className="text-sm font-bold uppercase tracking-wider">Enter access code</p>
                        </div>
                        <p className="text-sm text-[#525252] mb-4">
                            We emailed a 6-digit code to <span className="font-bold">{email}</span>. Code expires in 15 minutes.
                        </p>
                        <input
                            data-testid="share-otp-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                            placeholder="123456"
                            className="w-full border border-[#E5E5E5] px-3 py-3 mt-1 mb-3 text-center font-mono text-2xl tracking-[0.4em] focus:outline-none focus:border-cobalt"
                        />
                        <button
                            data-testid="share-verify-btn"
                            onClick={verify}
                            disabled={loading || otp.length < 4}
                            className="w-full bg-cobalt text-white text-sm font-bold uppercase tracking-wider py-2.5 hover:bg-cobalt/90 disabled:opacity-40"
                        >
                            {loading ? "Verifying…" : "Unlock resume"}
                        </button>
                        <button
                            onClick={() => { setOtp(""); setPhase("email"); }}
                            className="w-full mt-2 text-xs text-[#A3A3A3] hover:text-ink underline"
                        >
                            Wrong email? Start over
                        </button>
                    </div>
                )}

                {phase === "viewing" && (
                    <div className="bg-white shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-[#E5E5E5]">
                            <p className="text-sm text-[#525252]">
                                Viewing <span className="font-bold text-ink">{preview?.candidate_name}</span>'s resume
                            </p>
                            <button
                                data-testid="share-download-btn"
                                onClick={download}
                                className="inline-flex items-center gap-1.5 bg-cobalt text-white text-xs font-bold uppercase tracking-wider px-3 py-2 hover:bg-cobalt/90"
                            >
                                <DownloadSimple size={12} weight="bold" />
                                Download PDF
                            </button>
                        </div>
                        <iframe
                            data-testid="share-pdf-iframe"
                            src={pdfUrl}
                            title="Resume"
                            className="w-full h-[85vh] border-0"
                        />
                    </div>
                )}
            </main>

            <footer className="text-center text-[10px] text-[#A3A3A3] py-6">
                Powered by NaukriDaddy · Every view is logged for candidate privacy (DPDPA compliant)
            </footer>
        </div>
    );
}
